/**
 * Construtores de deep-link para a UI do PostHog, a partir do admin. Dão ao
 * super-admin um caminho de "drill-down ao vivo" — sair de um card agregado
 * (DAU, funil, retenção) e cair no PostHog nativo, onde dá pra explorar paths,
 * session replay e recortes que o BI pré-agregado não cobre.
 *
 * O `project id` numérico vem da env var `VITE_POSTHOG_PROJECT_ID` (pública —
 * vai no bundle, igual à `VITE_POSTHOG_KEY`). Sem ela, todos os builders
 * retornam `null` e o `OpenInPostHogButton` não renderiza — o admin segue
 * funcionando, só sem o atalho.
 *
 * Env é lido DENTRO das funções (não em const de topo) para ser sobrescrevível
 * em teste via `vi.stubEnv`, e por simetria com o cliente HogQL do backend.
 */

type RawEnv = Record<string, string | undefined>;

function env(): RawEnv {
  return import.meta.env as unknown as RawEnv;
}

function projectId(): string {
  return env().VITE_POSTHOG_PROJECT_ID ?? '';
}

/**
 * Host da UI do PostHog (app) derivado do host de INGESTÃO. O cliente posta
 * eventos em `us.i.posthog.com`, mas a UI fica em `us.posthog.com` — então
 * removemos o segmento `i.`. Funciona para `us`/`eu`; host custom cai no
 * fallback de `us`.
 */
function uiHost(): string {
  // Normaliza string vazia (não só null/undefined) para o default e tira
  // barra final (evita `//project` no concat).
  const raw = (env().VITE_POSTHOG_HOST ?? '').replace(/\/+$/, '');
  const ingest = raw === '' ? 'https://us.i.posthog.com' : raw;
  return ingest.replace('.i.posthog.com', '.posthog.com');
}

/**
 * True quando há project id configurado — os deep-links ficam ocultos sem ele.
 *
 * Origem: #68 — 2026-06-20 13:06 +0200 (commit d7f994997, deep-links do BI).
 *
 * Usado por: `posthogLinks.test.ts` apenas (as telas checam o `null` dos
 * builders via `OpenInPostHogButton`).
 */
export function hasPostHogProject(): boolean {
  return projectId().length > 0;
}

function projectUrl(path: string): string | null {
  const id = projectId();
  if (!id) return null;
  return `${uiHost()}/project/${id}${path}`;
}

type TrendMath = 'total' | 'dau';
export type TrendEvent = { id: string; math?: TrendMath };

/**
 * Trend (série temporal) pré-preenchido para um ou mais eventos.
 *
 * Origem: #68 — 2026-06-20 13:06 +0200 (commit d7f994997, deep-links do BI).
 *
 * Contrato: `null` sem `VITE_POSTHOG_PROJECT_ID`; `math` default `total`;
 * `dateFrom` no formato relativo do PostHog (`-30d`).
 *
 * Usado por (admin): `ProductDashboardPage`, `OperationDashboardPage`,
 * `AdminUsageDashboardPage`; `posthogLinks.test.ts`.
 *
 * Dados — exemplo de saída:
 *   `https://us.posthog.com/project/123/insights/new?insight=TRENDS&events=
 *   %5B%7B%22id%22%3A%22login%22…&date_from=-30d`
 */
export function posthogTrendUrl(
  events: TrendEvent[],
  dateFrom = '-30d',
): string | null {
  const payload = events.map((e) => ({
    id: e.id,
    type: 'events',
    math: e.math ?? 'total',
  }));
  return projectUrl(
    `/insights/new?insight=TRENDS&events=${encodeURIComponent(
      JSON.stringify(payload),
    )}&date_from=${encodeURIComponent(dateFrom)}`,
  );
}

/**
 * Funil pré-preenchido com os passos (eventos) na ordem dada.
 *
 * Origem: #68 — 2026-06-20 13:06 +0200 (commit d7f994997, deep-links do BI).
 *
 * Contrato: `null` sem project id; a ordem do array vira `order` do passo.
 *
 * Usado por (admin): `FunnelDashboardPage`; `posthogLinks.test.ts`.
 */
export function posthogFunnelUrl(eventIds: string[]): string | null {
  const payload = eventIds.map((id, order) => ({ id, type: 'events', order }));
  return projectUrl(
    `/insights/new?insight=FUNNELS&events=${encodeURIComponent(
      JSON.stringify(payload),
    )}`,
  );
}

/**
 * Retenção pré-preenchida sobre um evento alvo (default: qualquer atividade).
 *
 * Origem: #68 — 2026-06-20 13:06 +0200 (commit d7f994997, deep-links do BI).
 *
 * Contrato: `null` sem project id; default `$pageview`.
 *
 * Usado por (admin): `CohortsDashboardPage`; `posthogLinks.test.ts`.
 */
export function posthogRetentionUrl(targetEvent = '$pageview'): string | null {
  return projectUrl(
    `/insights/new?insight=RETENTION&target_entity=${encodeURIComponent(
      JSON.stringify({ id: targetEvent, type: 'events' }),
    )}`,
  );
}
